import { z } from 'zod';
// Transition token registry (§9). Used by media-track between clips. Wave 3
// will swap the renderer over to a registry of concrete CSS/Remotion transforms
// keyed by token; for now the value is just validated at the schema layer.
// `whip-up`: the incoming clip rushes up into place and settles, motion-blurred
// on the way. MEASURED off journey-2 at f662-694 by phase correlation — see
// TRANSITION_MOTION for the numbers and for why it is not the `slide` it looks
// like. It is a TRANSITION_TOKEN and not a mask token because it is a
// translation of the clip, not a shaped reveal of it.
export const TRANSITION_TOKENS = [
    'cut',
    'fade',
    'slide',
    'wipe',
    'whip-up',
];
export const transitionRef = z.enum(TRANSITION_TOKENS);
// ── Mask transitions ────────────────────────────────────────────────
//
// A SHAPED transition: the incoming clip is drawn over the outgoing one and
// revealed through an animated mask. `corner-l-wipe` is literally a cut
// between two clips (two blocks grow from opposite corners to form an L);
// `oval-iris` is the same mechanism with nothing behind it, i.e. black →
// footage at a reel's open.
//
// These are deliberately NOT members of TRANSITION_TOKENS. Widening that enum
// would oblige `TRANSITION_MOTION` (video-templates) and `TRANSITIONS`
// (remotion) — both exhaustive `Record<TransitionToken, …>` — to carry an
// entry per mask token, and the only thing they could hold is a SECOND copy of
// the geometry that already lives in `MOTION_PRESETS`. That duplication is the
// exact failure this registry keeps hitting, so a mask transition instead
// CITES the animation token and resolves to `MOTION_PRESETS[token]` at render
// time: re-tuning the preset re-tunes the transition, by construction.
//
// The list is written out rather than derived from `MOTION_PRESETS` because
// (a) deriving it would make this module import the preset table, which
// imports `TransitionToken` from here, and (b) a `readonly AnimationToken[]`
// filter result cannot give `z.enum` its literal union. `motion-presets.test.ts`
// asserts, in both directions, that this list is exactly the set of presets
// carrying a mask part — so it cannot silently drift.
export const MASK_TRANSITION_TOKENS = [
    'oval-iris',
    'corner-l-wipe',
    'bar-wipe-up',
    'bar-wipe-down',
    'bar-wipe-right',
    'bar-wipe-left',
    'split-bars',
    'thirds-cascade',
    'wedge-cross',
    'wedge-up-right',
];
export const maskTransitionRef = z.enum(MASK_TRANSITION_TOKENS);
export const maskTransitionSpecRef = z.object({
    mask: maskTransitionRef,
});
export const transitionSpecRef = z.union([
    transitionRef,
    maskTransitionSpecRef,
]);
/** Narrows a spec, including the `undefined` case every call site has. */
export function isMaskTransition(spec) {
    return typeof spec === 'object' && spec !== null && 'mask' in spec;
}
//# sourceMappingURL=transition-tokens.js.map